import { ReactNode, forwardRef } from 'react'
import { CheckCircleIcon, CircleXIcon, InfoIcon, TriangleAlertIcon, type LucideIcon } from 'lucide-react'

import { cn } from '@/components/new/lib/utils'

type AlertSeverity = 'default' | 'info' | 'warning' | 'error' | 'success'

type AlertProps = React.ComponentPropsWithoutRef<'div'> & {
    severity?: AlertSeverity
    icon?: LucideIcon | null
    footer?: ReactNode
}

const alertClassNames: Record<AlertSeverity, string> = {
    default: 'border-border/60 bg-muted',
    info: 'border-blue-500/15 bg-blue-50 dark:bg-blue-950',
    warning: 'border-amber-500/20 bg-amber-50 dark:bg-amber-950',
    error: 'border-red-500/15 bg-red-50 dark:bg-red-950',
    success: 'border-green-500/15 bg-green-50 dark:bg-green-950',
}

const alertIconClassNames: Record<AlertSeverity, string> = {
    default: 'text-muted-foreground',
    info: 'text-blue-600 dark:text-blue-400',
    warning: 'text-amber-600 dark:text-amber-400',
    error: 'text-red-600 dark:text-red-400',
    success: 'text-green-600 dark:text-green-400',
}

const alertIcons: Record<AlertSeverity, LucideIcon> = {
    default: InfoIcon,
    info: InfoIcon,
    warning: TriangleAlertIcon,
    error: CircleXIcon,
    success: CheckCircleIcon,
}

const Alert = forwardRef<HTMLDivElement, AlertProps>(
    ({ children, className, severity = 'default', icon, footer, ...props }, ref) => {
        const Icon = icon === null ? null : (icon ?? alertIcons[severity])

        return (
            <div
                ref={ref}
                data-slot="alert"
                data-severity={severity}
                className={cn('rounded-md border text-sm leading-5 text-foreground/80', alertClassNames[severity], className)}
                {...props}
            >
                <div className="flex gap-2.5 px-3 py-2.5">
                    {Icon && (
                        <Icon
                            data-slot="alert-icon"
                            className={cn('mt-0.5 size-4 shrink-0', alertIconClassNames[severity])}
                            aria-hidden="true"
                        />
                    )}
                    <div data-slot="alert-content" className="min-w-0 flex-1">
                        {children}
                    </div>
                </div>
                {footer}
            </div>
        )
    }
)
Alert.displayName = 'Alert'

export { Alert, type AlertProps, type AlertSeverity }
